import { formatRecordedDate } from '@/utils/recordedValues'
/**
 * Profile Page
 * Displays user profile information with tenant context.
 * Personal Information becomes editable (full name, phone) and persists via the
 * users service (`PUT /users/me`) — TASK-04 (4a).
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  User,
  Mail,
  Shield,
  Building2,
  Phone,
  Calendar,
  Monitor,
  MapPin,
  ChevronRight,
  KeyRound,
  LogOut,
  Pencil,
  Check,
  X,
  AlertCircle,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getRoleDisplayName, getRoleBadgeClass } from '@/types/auth.types'
import { useSessionStore } from '@/store/sessionStore'
import { formatRelativeTime } from '@/types/session.types'
import { getUserService } from '@/services/users'
import { toast } from '@/store/toastStore'
import { persistAuthUserPatch } from '@/utils/authUserPatch'

export default function ProfilePage() {
  const { user, logout } = useAuth()
  const { sessions, loadUserSessions, getActiveSessionCount } = useSessionStore()
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)

  // Local mirror of the editable display fields. Seeded from the auth user and
  // updated on save so the page reflects changes without a full reload.
  const [displayName, setDisplayName] = useState(user?.fullName ?? '')
  const [displayPhone, setDisplayPhone] = useState(user?.phone ?? '')

  // Edit-mode form state
  const [isEditing, setIsEditing] = useState(false)
  const [formName, setFormName] = useState(user?.fullName ?? '')
  const [formPhone, setFormPhone] = useState(user?.phone ?? '')
  const [errors, setErrors] = useState<{ fullName?: string }>({})
  const [isSaving, setIsSaving] = useState(false)

  // Load user sessions on mount
  useEffect(() => {
    if (user?.id) {
      setIsLoadingSessions(true)
      loadUserSessions(user.id).finally(() => setIsLoadingSessions(false))
    }
  }, [user?.id, loadUserSessions])

  if (!user) return null

  const activeSessionCount = getActiveSessionCount()
  const recentSessions = sessions.filter(s => s.status === 'active').slice(0, 3)

  // Get initials for avatar
  const initials = (displayName || user.fullName)
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Plan badge color
  const getPlanBadgeClass = () => {
    switch (user.planCode) {
      case 'enterprise':
        return 'badge-primary'
      case 'professional':
        return 'badge-secondary'
      case 'starter':
        return 'badge-accent'
      default:
        return 'badge-ghost'
    }
  }

  const startEdit = () => {
    setFormName(displayName)
    setFormPhone(displayPhone)
    setErrors({})
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setErrors({})
  }

  const handleSave = async () => {
    const nextErrors: { fullName?: string } = {}
    if (!formName.trim()) nextErrors.fullName = 'Full name is required'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setIsSaving(true)
    try {
      await getUserService().updateMe({
        fullName: formName.trim(),
        phone: formPhone.trim() || undefined,
      })
      setDisplayName(formName.trim())
      setDisplayPhone(formPhone.trim())
      persistAuthUserPatch({ fullName: formName.trim(), phone: formPhone.trim() })
      setIsEditing(false)
      toast.success('Profile updated', 'Your changes have been saved.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not update profile', message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-text-primary">My Profile</h1>
      </div>

      {/* Profile Card */}
      <div className="card card-elevated p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={displayName} className="w-full h-full rounded-full" />
            ) : (
              initials
            )}
          </div>

          {/* Basic Info */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-text-primary">{displayName}</h2>
            <p className="text-text-secondary">{user.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                {getRoleDisplayName(user.role)}
              </span>
              {user.isVerified && <span className="badge badge-success badge-sm">Verified</span>}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button type="button" onClick={() => logout()} className="btn btn-outline btn-sm gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="card card-elevated p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Personal Information</h3>
            {!isEditing && (
              <button type="button" onClick={startEdit} className="btn btn-ghost btn-sm gap-2">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  className={`input input-bordered w-full ${errors.fullName ? 'border-red-500' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
                <input
                  type="email"
                  value={user.email}
                  className="input input-bordered w-full opacity-60 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-text-tertiary mt-1">Email cannot be changed here</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Phone</label>
                <input
                  type="tel"
                  value={formPhone}
                  onChange={e => setFormPhone(e.target.value)}
                  className="input input-bordered w-full"
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={cancelEdit} className="btn btn-ghost gap-2">
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="btn btn-primary gap-2"
                >
                  {isSaving ? (
                    <>
                      <span className="loading loading-spinner loading-sm" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="text-xs text-text-tertiary">Full Name</p>
                  <p className="text-text-primary font-medium">{displayName}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="text-xs text-text-tertiary">Email Address</p>
                  <p className="text-text-primary font-medium">{user.email}</p>
                </div>
              </div>

              {displayPhone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-tertiary">Phone</p>
                    <p className="text-text-primary font-medium">{displayPhone}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="text-xs text-text-tertiary">Role</p>
                  <p className="text-text-primary font-medium">{getRoleDisplayName(user.role)}</p>
                </div>
              </div>

              {user.lastLoginAt && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-tertiary">Last Login</p>
                    <p className="text-text-primary font-medium">
                      {formatRelativeTime(user.lastLoginAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Organization */}
        <div className="card card-elevated p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Organization</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-primary-500 mt-0.5" />
              <div>
                <p className="text-xs text-text-tertiary">Firm Name</p>
                <p className="text-text-primary font-medium">{user.tenantName}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary-500 mt-0.5" />
              <div>
                <p className="text-xs text-text-tertiary">Firm Type</p>
                <p className="text-text-primary font-medium capitalize">
                  {user.tenantType.replace('_', ' ')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <span className="text-primary-500 text-lg">₹</span>
              </div>
              <div>
                <p className="text-xs text-text-tertiary">Subscription Plan</p>
                <span className={`badge ${getPlanBadgeClass()} capitalize`}>{user.planCode}</span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-primary-500 mt-0.5" />
              <div>
                <p className="text-xs text-text-tertiary">Member Since</p>
                <p className="text-text-primary font-medium">
                  {formatRecordedDate(user.createdAt, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="card card-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Active Sessions</h3>
            <p className="text-sm text-text-secondary">
              {activeSessionCount} active session{activeSessionCount !== 1 ? 's' : ''}
            </p>
          </div>
          <Link
            to="/sessions"
            className="btn btn-ghost btn-sm gap-1 text-primary-600 dark:text-primary-400"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoadingSessions ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg border border-surface-overlay"
              >
                <div className="skeleton w-10 h-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 w-48 rounded" />
                  <div className="skeleton h-3 w-32 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : recentSessions.length > 0 ? (
          <div className="space-y-3">
            {recentSessions.map(session => (
              <div
                key={session.sessionId}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  session.isCurrentSession
                    ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-surface-overlay'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-surface-overlay flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-text-secondary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-text-primary">
                        {session.browser || 'Browser details unavailable'} on{' '}
                        {session.os || 'System details unavailable'}
                      </p>
                      {session.isCurrentSession && (
                        <span className="badge badge-primary badge-xs">Current</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-tertiary">
                      {session.city && (
                        <>
                          <MapPin className="w-3 h-3" />
                          <span>{session.city}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>{formatRelativeTime(session.lastActivityAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-text-tertiary py-4">No active sessions found</p>
        )}
      </div>

      {/* Security Section */}
      <div className="card card-elevated p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Security</h3>
        <div className="space-y-3">
          <Link
            to="/settings/security"
            className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-surface-overlay flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                <KeyRound className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Change Password</p>
                <p className="text-xs text-text-tertiary">Update your account password</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
          </Link>

          <Link
            to="/sessions"
            className="flex items-center justify-between p-3 rounded-lg border border-surface-overlay hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-surface-overlay flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                <Monitor className="w-5 h-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Manage Sessions</p>
                <p className="text-xs text-text-tertiary">View and terminate active sessions</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
          </Link>
        </div>
      </div>
    </div>
  )
}
